import type {
  SkuActivationReadinessRequest,
  SkuActivationReadinessResponse,
  SkuActivationMutationEvidence,
  SkuActivationPlanSnapshot,
} from '@nadoutong/contracts';
import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const READINESS_PATH = '/v1/ops/sku-activations/readiness';
const PLANS_PATH = '/v1/ops/sku-activations/plans';

export interface CreateSkuActivationPlanRequest {
  environment: string;
  sku_ids: string[];
  org_id?: string;
  api_key_id?: string;
  config_ids: string[];
  allowed_upstream_ids: string[];
}

export function createSkuActivationPlan(request: CreateSkuActivationPlanRequest, fetchImpl: typeof fetch = fetch) {
  return opsApiRequest<{ plan: SkuActivationPlanSnapshot }>(PLANS_PATH, { method: 'POST', body: request }, fetchImpl);
}

export function loadSkuActivationPlan(planId: string, fetchImpl: typeof fetch = fetch) {
  return opsApiRequest<{ plan: SkuActivationPlanSnapshot }>(`${PLANS_PATH}/${encodeURIComponent(planId)}`, {}, fetchImpl);
}

export function completeSkuActivationMutation(planId: string, mutationId: string,
  evidence: SkuActivationMutationEvidence, fetchImpl: typeof fetch = fetch) {
  return opsApiRequest<{ plan: SkuActivationPlanSnapshot; replayed: boolean; mirror?: { status?: string; delivery_id?: string | null } }>(
    `${PLANS_PATH}/${encodeURIComponent(planId)}/mutations/${encodeURIComponent(mutationId)}/complete`,
    { method: 'POST', body: evidence }, fetchImpl,
  );
}

export function useCreateSkuActivationPlan() {
  const { fetchImpl } = useSession();
  return useMutation({ mutationFn: (request: CreateSkuActivationPlanRequest) => createSkuActivationPlan(request, fetchImpl) });
}

export function useLoadSkuActivationPlan() {
  const { fetchImpl } = useSession();
  return useMutation({ mutationFn: (planId: string) => loadSkuActivationPlan(planId, fetchImpl) });
}

export function useCompleteSkuActivationMutation() {
  const { fetchImpl } = useSession();
  return useMutation({ mutationFn: (input: { planId: string; mutationId: string; evidence: SkuActivationMutationEvidence }) =>
    completeSkuActivationMutation(input.planId, input.mutationId, input.evidence, fetchImpl) });
}

export function runSkuActivationReadiness(
  request: SkuActivationReadinessRequest,
  fetchImpl: typeof fetch = fetch,
): Promise<SkuActivationReadinessResponse> {
  return opsApiRequest<SkuActivationReadinessResponse>(READINESS_PATH, {
    method: 'POST',
    body: request,
  }, fetchImpl);
}

export function useSkuActivationReadiness(): UseMutationResult<
  SkuActivationReadinessResponse,
  Error,
  SkuActivationReadinessRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (request) => runSkuActivationReadiness(request, fetchImpl),
  });
}
